import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { PodcastShow } from '@domain/models/podcast'

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedDate = new Date(value)
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate
  }
  return undefined
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

export function fromFirestorePodcastShow(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): PodcastShow {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>
  const showId = typeof data['show_id'] === 'string'
    ? data['show_id']
    : typeof data['showId'] === 'string'
      ? data['showId']
      : docSnap.id

  return {
    uid: docSnap.id,
    showId,
    name: typeof data['name'] === 'string' ? data['name'] : '',
    description:
      typeof data['description'] === 'string'
        ? data['description']
        : undefined,
    publisher:
      typeof data['publisher'] === 'string' ? data['publisher'] : undefined,
    languages: toStringArray(data['languages']),
    imageUrl:
      typeof data['image_url'] === 'string'
        ? data['image_url']
        : typeof data['imageUrl'] === 'string'
          ? data['imageUrl']
          : undefined,
    spotifyUrl:
      typeof data['spotify_url'] === 'string'
        ? data['spotify_url']
        : typeof data['spotifyUrl'] === 'string'
          ? data['spotifyUrl']
          : undefined,
    spotifyUri:
      typeof data['spotify_uri'] === 'string'
        ? data['spotify_uri']
        : typeof data['spotifyUri'] === 'string'
          ? data['spotifyUri']
          : undefined,
    totalEpisodes:
      typeof data['total_episodes'] === 'number'
        ? data['total_episodes']
        : typeof data['totalEpisodes'] === 'number'
          ? data['totalEpisodes']
          : undefined,
    lastSyncedAt: toOptionalDate(data['last_synced_at'] ?? data['lastSyncedAt']),
    updatedAt: toOptionalDate(data['updated_at'] ?? data['updatedAt']),
    createdAt: toOptionalDate(data['created_at'] ?? data['createdAt']),
  }
}
